<template>
  <div class="row full-width q-mb-md">
    <QExpansionItem
      v-model="toggledStatus"
      class="full-width"
      dense
      header-class="bold component-expandable"
    >
      <template #header>
        <QItemSection>
          <div class="row q-gutter-sm">
            <strong>{{ title }}</strong>
            <span
              v-if="!derSpecs"
              class="text-caption text-grey-7 q-mb-xs"
            >
              Show {{ expanded ? "less" : "more" }}
            </span>
          </div>
        </QItemSection>
      </template>

      <QMarkupTable
        v-if="derSpecs"
        flat
        separator="none"
      >
        <thead>
          <tr class="text-left">
            <th>ID</th>
            <th>Name</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <template
            v-for="spec in data"
            :key="spec.id"
          >
            <tr>
              <td>{{ spec.id }}</td>
              <td>
                {{ spec.label }}
              </td>
              <td>
                <span class="row">
                  <AllUserDropdown
                    v-model="spec.newUser"
                    class="col-10"
                    clearable
                    :default-value="spec.newUser || {}"
                    dense
                    :emit-value="false"
                    label="User"
                    :organisation-id="organisationId"
                    :rules="[(val) => !!val || 'Must select an user']"
                  />
                </span>
              </td>
            </tr>
          </template>
        </tbody>
      </QMarkupTable>

      <QMarkupTable
        v-else
        flat
        separator="none"
      >
        <thead>
          <tr class="text-left">
            <th>ID</th>
            <th>Name</th>
            <th>Mention Streams</th>
            <th>
              <span v-if="streamGroups"> Slack/Teams </span>
              <span v-else> Schedules </span>
            </th>
            <th>Users with Access</th>
            <th>Assign To</th>
          </tr>
        </thead>
        <tbody>
          <tr class="border-bottom">
            <td />
            <td />
            <td />
            <td />
            <td />
            <td>
              <AllUserDropdown
                v-model="newUser"
                class="col-10"
                clearable
                dense
                :emit-value="false"
                label="User"
                :organisation-id="organisationId"
                @update:model-value="setUserForAllItems"
              />
            </td>
          </tr>
          <template
            v-for="group in data"
            :key="group.id"
          >
            <tr>
              <td>{{ group.id }}</td>
              <td>{{ group.label }}</td>
              <td>
                <BaseButton
                  v-if="group.streams"
                  dense
                  flat
                  @click="group.expanded = !group.expanded"
                >
                  <strong>{{ group.streams.length }}</strong>
                  <span class="text-caption q-pl-sm">
                    Show {{ group.expanded ? "less" : "more" }}
                  </span>
                </BaseButton>
              </td>
              <td v-if="streamGroups">
                {{ hasSlackOrTeamsForGroup(group) }}
              </td>
              <td v-else>
                {{ group.schedules }}
              </td>
              <td>
                <span v-if="scheduledReports">
                  {{ group.usersWithAccess }}
                </span>
              </td>
              <td>
                <span class="row full-width">
                  <AllUserDropdown
                    :key="group.newUser"
                    v-model="group.newUser"
                    class="col"
                    clearable
                    :default-value="group.newUser || {}"
                    dense
                    :emit-value="false"
                    label="User"
                    :organisation-id="organisationId"
                    @update:model-value="group.archive = !group.newUser"
                  />
                </span>
              </td>
            </tr>
            <template v-if="group.streams && group.expanded">
              <tr
                v-for="stream in group.streams"
                :key="stream.id"
              >
                <td>{{ stream.id }}</td>
                <td />
                <td>{{ stream.label }}</td>
                <td>
                  <span v-if="streamGroups">
                    {{ enabledIntegrationsByStream[stream.id] }}
                  </span>
                </td>
                <td>
                  <span v-if="streamGroups">
                    {{ stream.streamsUsers.length }}
                  </span>
                </td>
                <td />
                <td />
              </tr>
            </template>
          </template>
        </tbody>
      </QMarkupTable>
    </QExpansionItem>
    <div
      v-if="!toggledStatus"
      class="column q-pl-md"
    >
      <span>{{ data.length }} groups</span>
      <span>{{ streamsCount }} Streams</span>
    </div>
  </div>
</template>

<script>
import { BaseButton } from "shared/components/base";

import AllUserDropdown from "src/components/common/AllUserDropdown.vue";

const OBJECT_TYPE_LABELS = {
  MentionStreamGroups: "Mention Stream Groups",
  SocialStreamGroups: "Social Stream Groups",
  ScheduledReportSpecs: "Scheduled Reports",
  OrganisationReportSpecs: "CER Specs",
};

export default {
  name: "UserAssociationsTable",
  components: {
    AllUserDropdown,
    BaseButton,
  },
  props: {
    organisationId: {
      type: [Number, String],
      required: true,
    },
    data: {
      type: Array,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      expanded: false,
      newUser: null,
    };
  },
  computed: {
    scheduledReports() {
      return this.type === "ScheduledReportSpecs";
    },
    streamGroups() {
      return ["MentionStreamGroups", "SocialStreamGroups"].includes(this.type);
    },
    derSpecs() {
      return this.type === "OrganisationReportSpecs";
    },
    title() {
      return OBJECT_TYPE_LABELS[this.type] || "";
    },
    streamsCount() {
      return this.data.reduce(
        (total, group) => total + (group.streams?.length || 0),
        0
      );
    },
    toggledStatus: {
      get() {
        if (this.derSpecs) {
          return true;
        }

        return this.expanded;
      },
      set(value) {
        this.expanded = value;
      },
    },
    enabledIntegrationsByStream() {
      const valuesByStream = {};

      this.data.forEach((group) => {
        if (!group.streams) return;

        group.streams.forEach(({ id, slack, microsoftTeams }) => {
          valuesByStream[id] = slack || microsoftTeams ? "Yes" : "No";
        });
      });

      return valuesByStream;
    },
  },
  methods: {
    setUserForAllItems() {
      this.data.forEach((group) =>
        Object.assign(group, { newUser: this.newUser })
      );
    },
    hasSlackOrTeamsForGroup(group) {
      if (!group.streams) return "No";

      const hasNotification = group.streams.some(
        (stream) => this.enabledIntegrationsByStream[stream.id] === "Yes"
      );

      return hasNotification ? "Yes" : "No";
    },
  },
};
</script>
